import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { SharedStoreFacade } from 'app/main/shared/shared/store/shared-store.facade';
import { combineLatest, Observable, of, switchMap, tap } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { DOCUMENT_CONTRACT_CARD_SEARCH_PHRASE_MIN_LENGTH } from '../../../../constants/constants';
import { GlobalService } from '../../../../global/global.service';
import { userDataSelector } from '../../../../store/selectors/auth.selectors';
import { AdvanceFilterParamType, IAdvanceFilter } from '../../../shared/advanced-filters/components/advance-filter';
import { ISaveNewFilter } from '../../../shared/save-listing-filters/components/saved-filters/saved-filters.component';
import { IGetSavedListingFiltersResponseDTO, SavedFilterType } from '../../../shared/save-listing-filters/save-filters.dto';
import { SaveListingFilters } from '../../../shared/save-listing-filters/store/saved-filters.facade';
import { IColumnSettings, ICustomColumn, } from '../../../shared/shared/table/components/custom-columns-settings/column-settings';
import { SimpleTableCellComponent } from '../../../shared/shared/table/components/simple-table-cell/simple-table-cell.component';

import { ITablePagerOptions } from '../../../shared/shared/table/components/table-pager/ITablePagerOptions';
import { ITableCellProperties } from '../../../shared/shared/table/table-cell';
import { ITableColumn } from '../../../shared/shared/table/table-column';
import { IFlattenedCategories, IGetCategoriesItemDto } from '../../categories.dto';
import { SECTION_TYPE } from '../../contract-control/contract-control.service';
import { IGetContractControlDataResponse } from '../../contract-control/store/dto/contract-control-view';
import { DocumentColumnsLabelTranslations } from '../../settings/documenttype/document-type-settings-columns';
import { GroupsService } from '../../users/groups/groups.service';
import { ISearchableRequest } from '../../users/userslisting/dto/get-user-list.dto';
import { HttpActionState } from '../../users/userslisting/store/users-listing.reducers';
import { CategoryColumnComponent } from '../document-listing/table-cols-components/category-column/category-column.component';
import { DocumentColumnComponent } from '../document-listing/table-cols-components/document-column/document-column.component';
import { DocumentMetodikaStatusComponent } from '../document-listing/table-cols-components/document-metodika-status/document-metodika-status.component';
import { DocumentStatusComponent } from '../document-listing/table-cols-components/document-status/document-status.component';
import {
    LatestAcitivtyColumnComponent
} from '../document-listing/table-cols-components/latest-acitivty-column/latest-acitivty-column.component';
import {
    MetadataFieldColumnComponent
} from '../document-listing/table-cols-components/metadata-field-column/metadata-field-column.component';
import { OrganizationColumnComponent } from '../document-listing/table-cols-components/organiztaion-column/organiztaion-column.component';
import { OwnerColumnComponent } from '../document-listing/table-cols-components/owner-column/owner-column.component';
import { RecipientColumnComponent } from '../document-listing/table-cols-components/recipient-column/recipient-column.component';
import { ILinkContractControl, IUpdatePreviewColumnFormData } from '../documents.service';
import { IContractData } from '../dto/contract-data.dto';

import { DocumentInternalReminder, IDocumentDetailRootDTO, Tag, VisibilityEnum } from '../dto/document-details.dto';
import { IDocumentDto, IDocumentTypesDto, IUserGroupFilter, OwnerFilterType } from '../dto/document-listing.dto';
import { DocumentStatusesEnum } from '../dto/document-statuses.enum';
import { FilterDocumentStatusesEnum } from '../dto/filter-document-statuses.enum';
import * as CategoriesActions from './../../../../store/actions/categories.actions';
import * as AuthSelectors from './../../../../store/selectors/auth.selectors';
import * as SavedFiltersActions from './../../../shared/save-listing-filters/store/saved-filters.actions';
import * as SavedFiltersSelectors from './../../../shared/save-listing-filters/store/saved-filters.selectors';

import { FieldData } from '../../edit-template/edit-template.dto';
import { IDocumentEntitySettings } from '../document-right-sidebar/document-details/document-details.dto';
import * as DocumentBulkUploadActions from './../modals/documents-bulk-upload/store/documents-bulk-upload.actions';
import * as DocumentListingActions from './document-listing.actions';
import { DocumentDetailActions } from './document-listing.actions';
import { DocumentViewVariantEnum, IDocumentFilterState, IExportDocumentsRequest } from './document-listing.reducers';
import * as DocumentListingSelectors from './document-listing.selectors';
import * as DocumentsListingSelectors from './document-listing.selectors';
import { GetDocumentsRequestBuilder } from './get-documents-request-builder';
import { TemplateNameComponent } from '../document-listing/table-cols-components/template-name/template-name.component';
import { IContractControlData } from '../../contract-control/contract-control.dto';

export interface IConnectContranct {
    contract_control_id: number,
    sectionable_id: number,
    section_type: string,
    contract_control: IContractControlData
}


export interface IUpdateFieldsData {
    dataable_type: string;
    dataable_id: number;
    field_type: string;
    data?: FieldData[];
    filteredFields?: FieldData[];
    deleted_field_uuid?: string[];
}

export interface ILinkContract {
    contract_control_id: number;
    sectionable_id: number;
    section_type: SECTION_TYPE;
    contract_control: ILinkContractControl
}

@Injectable({
    providedIn: 'root'
})
export class DocumentListingFacade {


    specialAction$: Observable<string | null> = this._store.select(DocumentListingSelectors.specialAction);
    activeView$: Observable<DocumentViewVariantEnum> = this._store.select(DocumentListingSelectors.activeView);
    selection$ = this._store.select(DocumentListingSelectors.selection);
    documentsList$: Observable<IDocumentDto[]> = this._store.select(DocumentListingSelectors.documents);
    paging$: Observable<ITablePagerOptions> = this._store.select(DocumentListingSelectors.paging);
    isLoading$: Observable<boolean> = combineLatest({
        data: this._store.select(DocumentListingSelectors.isLoading),
        settings: this._store.select(DocumentListingSelectors.loadingColumnsSettings)
    }).pipe(map(x => x.data || x.settings));
    isLoadingDone$ = this._store.select(DocumentListingSelectors.isLoadingDone);
    search$: Observable<string> = this._store.select(DocumentListingSelectors.search);
    filterByStatus$: Observable<FilterDocumentStatusesEnum | null> = this._store.select(DocumentListingSelectors.filterByStatus);
    filterBy$: Observable<IDocumentFilterState> = this._store.select(DocumentListingSelectors.filterBy);
    documentTypes$: Observable<IDocumentTypesDto[]> = this._sharedStoreFacade.documentTypes$;
    ownerSearch$: Observable<string> = this._store.select(DocumentsListingSelectors.ownerFilterSearch);
    allFlattenCategories$: Observable<IGetCategoriesItemDto[]> = this._store.select(DocumentListingSelectors.categories);
    categories$: Observable<IGetCategoriesItemDto[]> =
        combineLatest({
            allCategories: this._store.select(DocumentListingSelectors.categories),
            byDocumentType: this._store.select(DocumentListingSelectors.categoriesByDocId)
        })
            .pipe(
                map(({ allCategories, byDocumentType }) => {
                    if (byDocumentType && byDocumentType.length > 0) {
                        return allCategories.filter(x => x.id === null || byDocumentType.includes(x.id));
                    } else {
                        return allCategories;
                    }
                })
            );
    isDocumentTypesLoading$: Observable<boolean> = this._sharedStoreFacade.isDocumentTypesLoading$;
    ownerFilterDDL$: Observable<IUserGroupFilter[]> = combineLatest({
        users: this._store.select(DocumentsListingSelectors.ownerFilterDDL),
        groups: this._store.select(AuthSelectors.isTenantOwner).pipe(
            switchMap((isTenantOwner) => {
            if (isTenantOwner) {
                return this._groupsService.fetchGroups()
                .pipe(
                    map((x: { name: string, id: number }[]) => (x || []).map(y => ({
                    id: y.id,
                    name: y.name,
                    group: OwnerFilterType.group,
                    }))),
                )
            } else {
                return this._store.select(DocumentListingSelectors.userGroups)
                .pipe(map(x => (x || []).map(y => ({
                    id: y.id,
                    name: y.name,
                    group: OwnerFilterType.group,
                }))))
            }
            })),
        me: this._store.select(userDataSelector)
        }).pipe(
        map(x => {
            const users = x.users || [];
            const groups = x.groups || [];
            const meId = x.me?.id ?? null;

            return [
            { id: null, group: '', name: 'All' } as IUserGroupFilter,
            { id: meId, group: OwnerFilterType.me, name: 'me' } as IUserGroupFilter,
            ...users,
            ...groups
            ] as IUserGroupFilter[];
        })
    );
    orderedColumns$: Observable<IColumnSettings[]> = this._store.select(DocumentListingSelectors.orderedColumns);
    allColumns$: Observable<IColumnSettings[]> = this._store.select(DocumentListingSelectors.allColumns);
    mappedColumns$: Observable<ITableColumn[]> = this._store.select(DocumentListingSelectors.orderedColumns).pipe(map(this.mapOrderedColumns.bind(this)));
    isDeletingDocumentsInProgress$ = this._store.select(DocumentListingSelectors.isDeletingDocumentsInProgress);
    isRestoringDocumentsInProgress$ = this._store.select(DocumentListingSelectors.isRestoringDocumentsInProgress);

    documentIndexData$: Observable<IDocumentDetailRootDTO> = this._store.select(DocumentListingSelectors.documentIndexData);
    documentIndexDataRequestState$: Observable<HttpActionState> = this._store.select(DocumentListingSelectors.documentIndexDataRequestState);

    /** For right bar */
    selectedDocument$: Observable<IDocumentDetailRootDTO> = this._store.select(DocumentListingSelectors.getSelectedDocument);
    documentDataLoader$: Observable<boolean> = this._store.select(DocumentListingSelectors.documentDataLoader);

    /** wizard form selectors */
    contractData$: Observable<IContractData> = this._store.select(DocumentListingSelectors.contractData);

    advancedFiltersList$ = this._store.select(DocumentListingSelectors.advancedFilterList);
    isAdvancedFiltersListLoading$ = this._store.select(DocumentListingSelectors.isAdvancedFiltersListLoading);
    appliedAdvancedFilters$: Observable<IAdvanceFilter<AdvanceFilterParamType>[]> = this._store.select(DocumentListingSelectors.appliedAdvancedFilters);

    savedFilters$: Observable<IGetSavedListingFiltersResponseDTO[]> = this._store.select(SavedFiltersSelectors.filtersByType(SavedFilterType.Document));
    selectedSavedFilterId$: Observable<number> = this._store.select(SavedFiltersSelectors.selectedFilter).pipe(map(x => x?.id || null));
    lastSelectedDocument$ = this._store.select(DocumentListingSelectors.lastSelectedDocument);
    hasAnyFiltersApplied$: Observable<boolean> = combineLatest([
        this._store.select(DocumentListingSelectors.search).pipe(map(x => x?.trim().length > 0)),
        this.appliedAdvancedFilters$.pipe(map(x => x?.length > 0)),
        this.filterBy$.pipe(map(x => this.isAnyDefaultFilterSet(x)))
    ]
    ).pipe(map(x => x.some(y => y === true)));

    constructor(private _store: Store,
        private _groupsService: GroupsService,
        private _transloco: TranslocoService,
        private _sharedStoreFacade: SharedStoreFacade,
        private _saveListingFiltersFacade: SaveListingFilters,
        private _globalService: GlobalService
    ) {
    }

    changePage(pageNumber: number): void {
        this._store.dispatch(DocumentListingActions.changePage(pageNumber));
    }

    selectDocument(id: number, rightBarName: string = 'document-right-sidebar'): void {
        this._store.dispatch(DocumentListingActions.documentClicked(id, rightBarName))
    }

    documentIndexData(id: number): void {
        this._store.dispatch(DocumentListingActions.documentIndexDataCall(id));
    }

    documentRevoke(documentId: number): void {
        this._store.dispatch(DocumentListingActions.makeDocumentRevoked(documentId, DocumentStatusesEnum.Revoke));
    }

    fetchDocuments() {
        this._store.dispatch(DocumentListingActions.fetchDocuments());
    }

    fetchDocumentTypes() {
        this._store.dispatch(DocumentListingActions.fetchDocumentTypes());
    }

    fetchFilterData() {
        this._store.dispatch(DocumentListingActions.fetchUserGroups());
        this._store.dispatch(CategoriesActions.fetchCategories());
    }

    filterByStatus(status: FilterDocumentStatusesEnum) {
        this._store.dispatch(DocumentListingActions.filterByStatus(status));
    }

    setPerPageRecords(perPageRecords: number) {
        this._store.dispatch(DocumentListingActions.setPerPageRecords(perPageRecords));
    }

    filterByFromDate(date: string) {
        this._store.dispatch(DocumentListingActions.filterByFromDate(date));
    }

    filterByToDate(date: string) {
        this._store.dispatch(DocumentListingActions.filterByToDate(date));
    }

    filterByDocumentType(id: number) {
        this._store.dispatch(DocumentListingActions.filterByDocumentType(id));
    }

    filterByUser(id: IUserGroupFilter) {
        this._store.dispatch(DocumentListingActions.filterByUser(id));
    }

    setBasicDocumentFilters(filters: Partial<IDocumentFilterState & ISearchableRequest>): void {
        this._store.dispatch(DocumentListingActions.setBasicDocumentFilters({ filters }))
    }

    setAdvancedFilters(filters: Partial<IAdvanceFilter<AdvanceFilterParamType>[]>): void {
        this._store.dispatch(DocumentListingActions.setAdvancedDocumentFilters({ filters }));
    }

    onSearchChange(searchValue: string) {
        if (this.checkSearchPhraseValidity(searchValue)) {
            this._store.dispatch(DocumentListingActions.searchChange(searchValue));
        } else {
            this._store.dispatch(DocumentListingActions.fetchDocumentsFailed(this._transloco.translate('Document and Contract Card - search length validation', { minLength: DOCUMENT_CONTRACT_CARD_SEARCH_PHRASE_MIN_LENGTH })
            ));
        }
    }

    changeActiveView(view: DocumentViewVariantEnum) {
        this._store.dispatch(DocumentListingActions.changeActiveView(view));
    }

    selectionChanged(documents: IDocumentDto[]): void {
        this._store.dispatch(DocumentListingActions.documentsSelected(documents));
    }

    deleteSelectedDocuments() {
        this._store.dispatch(DocumentListingActions.deleteSelectedDocuments());
    }

    restoreSelectedDocuments() {
        this._store.dispatch(DocumentListingActions.restoreSelectedDocuments())
    }

    updateColumnsSettings(column_settings: IColumnSettings[], skipSkeleton: boolean = true) {
        this._store.dispatch(DocumentListingActions.updateCustomColumns({ column_settings, skipSkeleton }))
    }


    updateCurrentFormStatus(status: boolean) {
        this._store.dispatch(DocumentListingActions.updateFormStatus(status))
    }


    updateCurrentFormStep(step: number = 0) {
        this._store.dispatch(DocumentListingActions.updateFormStep(step));
    }


    updateCurrentFormSubmittion(isSubmitted: boolean = false) {
        this._store.dispatch(DocumentListingActions.updateFormSubmittion(isSubmitted));
    }


    setDocumentType(documentType: IDocumentTypesDto) {
        this._store.dispatch(DocumentListingActions.setDocumentType(documentType));
    }


    setDocumentData(key: string, contractData: number | string) {
        this._store.dispatch(DocumentListingActions.setContractData(key, contractData));
    }


    updateContractFirstForm(key: string, data: string) {
        this._store.dispatch(DocumentListingActions.updateContractForm(key, data));
    }

    softDelete(documentId: number) {
        this._store.dispatch(DocumentListingActions.deleteDocument({ documentId }));
    }

    deletePermanently(documentId: number) {
        this._store.dispatch(DocumentListingActions.deleteDocument({ documentId }));
    }

    connectContract(formData: IConnectContranct) {
        this._store.dispatch(DocumentListingActions.connectContract(formData as ILinkContract));
    }

    updateDocumentDate(documentId: number, due_date: string) {
        this._store.dispatch(DocumentListingActions.updateDocumentDueDate(documentId, due_date));
    }

    updateDocumentData(documentId: number, document: IDocumentDto) {
        this._store.dispatch(DocumentListingActions.updateDocumentData(documentId, document));
    }

    linkContract(contract: ILinkContract) {
        this._store.dispatch(DocumentListingActions.connectContractSuccess([contract]))
    }

    updateDocumentVisibility(documentId: number, visibility: VisibilityEnum) {
        this._store.dispatch(DocumentListingActions.updateDocumentVisibility(documentId, visibility));
    }


    updateDocumentTag(documentId: number, tag: Tag) {
        this._store.dispatch(DocumentListingActions.updateTagsInDocument(documentId, tag));
    }


    removeTagFromDocument(documentId: number, tag: Tag) {
        this._store.dispatch(DocumentListingActions.removeTagFromDocument(documentId, tag));
    }


    updateDocumentStatus(documentId: number, documentStatus: DocumentStatusesEnum) {
        this._store.dispatch(DocumentListingActions.updateDocumentStatus(documentId, documentStatus))
    }




    updateDocumentType(documentId: number, documentType: IDocumentTypesDto) {
        this._store.dispatch(DocumentListingActions.updateDocumentType(documentId, documentType));
    }

    updateDocumentName(documentId: number, name: string) {
        this._store.dispatch(DocumentListingActions.updateDocumentName(documentId, name));
    }

    private mapOrderedColumns(columns: IColumnSettings<{ meta_data_name, meta_data_type, api_attribute }>[]): ITableColumn[] {
        const statusExists = columns.some(column => column.field_name === 'status');
        if (!statusExists) {
            const statusColumn = {
                order: columns.length,
                uuid: '0',
                doc_id: null,
                field_name: 'status',
                label: DocumentColumnsLabelTranslations.status,
                status: 1,
                type: 'default'
            }
            columns.push(statusColumn);
        }
        return columns.map(x => {
            return ({
                title: this._transloco.translate(x.label),
                field: x.field_name,
                cell: this.getCellComponent(x),
                details: x
            })
        })
    }

    private getCellComponent(column: IColumnSettings<{ meta_data_name, meta_data_type, api_attribute }>): ITableCellProperties {
        const isMetadataColumn = this.isMetadataColumn(column);
        if (isMetadataColumn) {
            return MetadataFieldColumnComponent;
        } else {
            switch (column.field_name) {
                case 'document_type':
                    return DocumentColumnComponent;
                case 'template_name':
                    return TemplateNameComponent;
                case 'user':
                    return OwnerColumnComponent;
                case 'recipient_roles':
                    return RecipientColumnComponent;
                case 'organization':
                    return OrganizationColumnComponent;
                case 'status':
                    return DocumentStatusComponent;
                case 'metodika_status':
                    return DocumentMetodikaStatusComponent;
                case 'status_updated_at':
                    return LatestAcitivtyColumnComponent;
                case 'category_name':
                    return CategoryColumnComponent;
                default:
                    return SimpleTableCellComponent
            }
        }
    }

    private isMetadataColumn(column: IColumnSettings<{ meta_data_name, meta_data_type, api_attribute }>): boolean {
        return column?.details?.meta_data_name
            && column?.details?.meta_data_type
            && column?.details?.api_attribute;
    }

    fetchColumnSettings() {
        this._store.dispatch(DocumentListingActions.fetchCustomColumns());
    }

    exportCSV(format: 'csv' | 'xlsx' = 'csv') {
        this._store.dispatch(DocumentListingActions.exportCSV({ format }));
    }

    exportData(exportOptions: IExportDocumentsRequest) {
        this._store.dispatch(DocumentListingActions.exportDocumentsData({ exportOptions }))
    }

    closeBulkUploadModal() {
        this._store.dispatch(DocumentBulkUploadActions.resetState());
    }

    resetState() {
        this._store.dispatch(DocumentListingActions.resetState());
        this._store.dispatch(SavedFiltersActions.resetState())
    }

    fetchAdvancedFilters() {
        this._store.dispatch(DocumentListingActions.fetchAdvancedFilters());
    }

    advancedFilterSearch(searchString: string) {
        this._store.dispatch(DocumentListingActions.advancedFilterSearch({ searchString }));
    }

    addNewFilter(filter: Omit<IAdvanceFilter<AdvanceFilterParamType>, 'uuid'>) {
        this._store.dispatch(DocumentListingActions.addAdvancedFilter({ filter }));
    }

    onRemoveFilter(filter: IAdvanceFilter<AdvanceFilterParamType>) {
        this._store.dispatch(DocumentListingActions.removeAdvancedFilter({ filter }));
    }

    updateAdvancedFilter(filter: IAdvanceFilter<AdvanceFilterParamType>) {
        this._store.dispatch(DocumentListingActions.updateAdvancedFilter({ filter }));
    }

    onFilterClose(filter: Partial<IAdvanceFilter<AdvanceFilterParamType>>) {
        this._store.dispatch(DocumentListingActions.onFilterClose({ filter }));
    }

    fetchSavedFilters() {
        this._saveListingFiltersFacade.fetchSavedFilters();
    }

    saveFilter(filterParams: ISaveNewFilter) {
        combineLatest({
            search: this._store.select(DocumentsListingSelectors.search),
            paging: this._store.select(DocumentsListingSelectors.paging),
            filters: this._store.select(DocumentsListingSelectors.filterBy),
            advancedFilters: this._store.select(DocumentsListingSelectors.appliedAdvancedFilters),
        })
            .pipe(
                take(1),
                switchMap(({ filters, advancedFilters, search }) => {
                    const filterValue = new GetDocumentsRequestBuilder()
                        .setSearchValue(search)
                        .setAdvancedFilter(advancedFilters)
                        .setBasicFilter(filters)
                        .build();

                    return of(filterValue);
                }),
                tap(x => {
                    if (!x) {
                        this._globalService.toastrWarning(this._transloco.translate('No filter applied toast (saved filters modal) - You have to apply some filters first'))
                    }
                }),
                filter(x => x != null),

            )
            .subscribe((value) => {
                this._saveListingFiltersFacade.saveFilter({
                    ...filterParams,
                    type: SavedFilterType.Document,
                    value: value
                });
            });
    }

    restoreFilter(filterId: number) {
        this._store.dispatch(DocumentListingActions.restoreFilter({ filterId }));
    }

    editFilter(filter: IGetSavedListingFiltersResponseDTO) {
        this._store.dispatch(DocumentListingActions.editFilter({ filter }));
    }

    deleteFilter(filterId: number) {
        this._store.dispatch(DocumentListingActions.deleteFilter({ filterId }));
    }

    filterByCategory(id: number) {
        this._store.dispatch(DocumentListingActions.filterByCategory(id));
    }

    fetchCategories() {
        this._store.dispatch(CategoriesActions.fetchCategories());
    }

    onOwnerSearchChange(searchValue: string) {
        this._store.dispatch(DocumentListingActions.ownerSearchChanged(({ search: searchValue })));
    }

    onUpdateInternalReminder(reminders: DocumentInternalReminder[]) {
        this._store.dispatch(DocumentListingActions.updateInternalReminder(reminders));
    }

    updateDocumentPreviewColumnValue(formData: IUpdatePreviewColumnFormData): void {
        this._store.dispatch(DocumentListingActions.updateDocumentColumnValue({ formData }));
    }

    private checkSearchPhraseValidity(searchString: string) {
        return searchString?.trim()?.length >= DOCUMENT_CONTRACT_CARD_SEARCH_PHRASE_MIN_LENGTH || !searchString;
    }

    updateFieldsData(payload: IUpdateFieldsData): void {
        this._store.dispatch(DocumentListingActions.updateFieldsData({ payload }));
    }

    private isAnyDefaultFilterSet(data: IDocumentFilterState): boolean {
        const isStatusSet = () => {
            return data.status != null && (data.status as string) != ''
        }
        const isDocumentTypeSet = () => {
            return data.documentTypeId != null && data.documentTypeId != 0;
        }

        const isFromDateSet = () => {
            return data.dateFrom != null;
        }

        const isToDateSet = () => {
            return data.dateTo != null
        }

        const isOwnerSet = () => {
            return data.userFilter != null && data.userFilter?.id != null;
        }

        const isCategorySet = () => {
            return data.categoryId != null;
        }






        return [
            isStatusSet(),
            isDocumentTypeSet(),
            isFromDateSet(),
            isToDateSet(),
            isOwnerSet(),
            isCategorySet()
        ].some(x => x === true)

    }

    checkSpecialDocumentActions(documentId: number) {
        this._store.dispatch(DocumentListingActions.checkSpecialActions({ documentId }));
    }

    resendToMetodika(documentId: number) {
        this._store.dispatch(DocumentListingActions.resendToMetodika({ documentId }));
    }




    saveDocumentEntitySetting(documentId: number, settings: IDocumentEntitySettings): void {
        this._store.dispatch(DocumentDetailActions.updateDocumentEntityData({ documentId, settings }));
    }
    
    changeDocumentType(id: number, documentId: number): void {
        this._store.dispatch(DocumentDetailActions.changeDocumentType({ id, documentId }));
    }


    setCustomColumns(columns: IColumnSettings[]): void {
        const column_settings: ICustomColumn = {
            column_settings: columns,
            order_settings: columns
        }
        this._store.dispatch(DocumentListingActions.fetchCustomColumnsSucceeded({ columns: column_settings }));
    }
}
